export const getFormattedDate = () => {
  const date = new Date();
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${date.getFullYear()}`;
};

const isSingleDay = (days: number | string) => Number(days) === 1;

// Per-request-type PDF header and leave-days noun, modeled directly on the
// Annual Leave PDF ("CERERE CONCEDIU DE ODIHNĂ" / "zi(le) de concediu de
// odihnă"). Falls back to the Annual Leave copy for any unrecognized type.
const LEAVE_TYPE_PDF_COPY: Record<
  string,
  { header: string; singular: string; plural: string }
> = {
  ANNUAL_LEAVE: {
    header: 'CERERE CONCEDIU DE ODIHNĂ',
    singular: 'zi de concediu de odihnă',
    plural: 'zile de concediu de odihnă',
  },
  MARRIAGE: {
    header: 'CERERE CONCEDIU PENTRU CĂSĂTORIE',
    singular: 'zi de concediu pentru căsătorie',
    plural: 'zile de concediu pentru căsătorie',
  },
  CHILD_BIRTH: {
    header: 'CERERE CONCEDIU PATERNAL',
    singular: 'zi de concediu paternal',
    plural: 'zile de concediu paternal',
  },
  DEATH_OF_RELATIVE: {
    header: 'CERERE CONCEDIU PENTRU DECES',
    singular: 'zi de concediu pentru deces',
    plural: 'zile de concediu pentru deces',
  },
  BLOOD_DONATION: {
    header: 'CERERE CONCEDIU PENTRU DONARE DE SÂNGE',
    singular: 'zi de concediu pentru donare de sânge',
    plural: 'zile de concediu pentru donare de sânge',
  },
};

// Unpaid Leave has two reasons, each with its own PDF header/wording -
// distinct from the other 5 types, which don't vary within themselves.
// Falls back to PROFESSIONAL_TRAINING's copy for an unrecognized/missing
// reason (should only happen for pre-existing requests created before the
// reason field existed).
const UNPAID_LEAVE_PDF_COPY_BY_REASON: Record<
  string,
  { header: string; singular: string; plural: string }
> = {
  PROFESSIONAL_TRAINING: {
    header: 'CERERE CONCEDIU FĂRĂ PLATĂ - FORMARE PROFESIONALĂ/STUDII',
    singular: 'zi de concediu fără plată pentru formare profesională/studii',
    plural: 'zile de concediu fără plată pentru formare profesională/studii',
  },
  PERSONAL_STUDIES: {
    header: 'CERERE CONCEDIU FĂRĂ PLATĂ - NEVOI PERSONALE',
    singular: 'zi de concediu fără plată pentru nevoi personale',
    plural: 'zile de concediu fără plată pentru nevoi personale',
  },
};

const getUnpaidLeaveCopy = (unpaidLeaveReason?: string) =>
  (unpaidLeaveReason && UNPAID_LEAVE_PDF_COPY_BY_REASON[unpaidLeaveReason]) ||
  UNPAID_LEAVE_PDF_COPY_BY_REASON.PROFESSIONAL_TRAINING;

export const getLeaveTypePdfHeader = (
  requestType?: string,
  unpaidLeaveReason?: string,
) => {
  if (requestType === 'UNPAID_LEAVE') {
    return getUnpaidLeaveCopy(unpaidLeaveReason).header;
  }
  return (
    (requestType && LEAVE_TYPE_PDF_COPY[requestType]?.header) ||
    LEAVE_TYPE_PDF_COPY.ANNUAL_LEAVE.header
  );
};

export const getRomanianLeaveDaysText = (
  days: number | string,
  requestType?: string,
  unpaidLeaveReason?: string,
) => {
  const copy =
    requestType === 'UNPAID_LEAVE'
      ? getUnpaidLeaveCopy(unpaidLeaveReason)
      : (requestType && LEAVE_TYPE_PDF_COPY[requestType]) ||
        LEAVE_TYPE_PDF_COPY.ANNUAL_LEAVE;
  return isSingleDay(days) ? copy.singular : copy.plural;
};

export const getRomanianDateOrPeriodText = (days: number | string) =>
  isSingleDay(days) ? ' data de' : ' perioada';

export const getRomanianYearSplitText = (days: number | string) =>
  isSingleDay(days) ? 'zi aferentă anului' : 'zile aferente anului';

export const getGenderWords = (gender?: string) => {
  if (gender === 'male') {
    return {
      subject: 'Subsemnatul',
      employee: 'angajat al',
      replace: 'În perioada absenței voi fi înlocuit de către',
      signature: 'Angajat',
    };
  }

  if (gender === 'female') {
    return {
      subject: 'Subsemnata',
      employee: 'angajată a',
      replace: 'În perioada absenței voi fi înlocuită de către',
      signature: 'Angajată',
    };
  }

  return {
    subject: 'Subsemnatul/a',
    employee: 'angajat/ă',
    replace: 'În perioada absenței voi fi înlocuit/ă de către',
    signature: 'Angajat/ă',
  };
};
